// Racha de turnos documentados — el "cierre gratificante" del turno: cada vez que
// cierras y guardas tu turno, sube tu conteo y tu racha. Da sensación de avance
// y engancha a volver. Se guarda en profiles (RLS propia del perfil).
//
// La racha tolera el calendario irregular de enfermería: no exige días
// consecutivos, solo que no pase demasiado tiempo entre turnos (ventana de 10
// días). Así un turno cada 3-4 días mantiene la racha; un parón largo la reinicia.

import { supabase } from './supabase';

export const VENTANA_RACHA_DIAS = 10;

// Días entre dos fechas 'YYYY-MM-DD' (b - a). null si alguna es inválida.
export function diasEntre(a, b) {
  if (!a || !b) return null;
  const da = new Date(`${a}T00:00:00`);
  const db = new Date(`${b}T00:00:00`);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.round((db - da) / 86400000);
}

// Clave de fecha local 'YYYY-MM-DD'.
export function fechaKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Cálculo PURO de la nueva racha al cerrar un turno hoy.
//  prev = { total, racha, fecha } (fecha 'YYYY-MM-DD' del último cierre o null).
// Devuelve { total, racha, fecha, subioRacha }.
export function siguienteRacha(prev = {}, hoy = fechaKey()) {
  const total = Number(prev.total) || 0;
  const racha = Number(prev.racha) || 0;
  const fecha = prev.fecha || null;
  const gap = diasEntre(fecha, hoy);

  let nuevaRacha;
  let subioRacha;
  if (gap === null) {
    nuevaRacha = 1; subioRacha = true;            // primer turno registrado
  } else if (gap === 0) {
    nuevaRacha = racha || 1; subioRacha = false;  // mismo día: no dobla la racha
  } else if (gap >= 1 && gap <= VENTANA_RACHA_DIAS) {
    nuevaRacha = (racha || 0) + 1; subioRacha = true;
  } else {
    nuevaRacha = 1; subioRacha = true;            // parón largo (o fecha futura): reinicia
  }

  return { total: total + 1, racha: nuevaRacha, fecha: hoy, subioRacha };
}

// Lee la racha actual del perfil. Best-effort: ceros si falla.
export async function cargarRacha(userId) {
  if (!userId) return { total: 0, racha: 0, fecha: null };
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('turnos_documentados, turno_racha, turno_racha_fecha')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return { total: 0, racha: 0, fecha: null };
    return {
      total: Number(data.turnos_documentados) || 0,
      racha: Number(data.turno_racha) || 0,
      fecha: data.turno_racha_fecha || null,
    };
  } catch {
    return { total: 0, racha: 0, fecha: null };
  }
}

// Registra un turno cerrado: sube conteo y racha y persiste. Devuelve el nuevo
// estado { total, racha, fecha, subioRacha }. Best-effort: si falla la BD,
// devuelve igualmente el cálculo para que la UI celebre (no bloquea el cierre).
export async function registrarTurnoCerrado(userId) {
  const prev = await cargarRacha(userId);
  const next = siguienteRacha(prev);
  if (userId) {
    try {
      await supabase
        .from('profiles')
        .update({
          turnos_documentados: next.total,
          turno_racha: next.racha,
          turno_racha_fecha: next.fecha,
        })
        .eq('user_id', userId);
    } catch { /* best-effort */ }
  }
  return next;
}
